import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TiersType } from '@core-lib/enums/tiers.enum';
import { extractToList, filtreTiersListForOperation, filtreTiersListForUniqueTiersPartieValue } from '@core-lib/functions';
import { IList } from '@core-lib/models/list.model';
import { INameBySiret, ITiers, ITiersPersonneMoraleForm, ITiersPersonnePhysiqueForm } from '@core-lib/models/tiers.model';
import { environment } from '@environments/environment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthSharedService } from './auth.service';
import { ConfirmationService } from './confirmation-service.service';
import { ICoordonneeBancaire } from '@core-lib/models';

@Injectable({
	providedIn: 'root'
})
export class TiersService {
	get tenantKey() {
		return this.authService.currentTenantKey;
	}

	constructor(
		private http: HttpClient,
		private authService: AuthSharedService,
		private confirmationService: ConfirmationService
	) {}

	createTiers(tiers: ITiersPersonnePhysiqueForm | ITiersPersonneMoraleForm, structureId?: string): Observable<ITiers> {
		const params = {
			...tiers,
			structureId
		};

		if (this.determineIfIsTiersPhysique(tiers)) {
			return this.http.post<ITiers>(`${environment.apiUrl}/t/${this.tenantKey}/tiers/personnephysique`, params);
		} else {
			return this.http.post<ITiers>(`${environment.apiUrl}/t/${this.tenantKey}/tiers/personneMorale`, params);
		}
	}

	updateTiers(
		tiers: ITiersPersonnePhysiqueForm | ITiersPersonneMoraleForm,
		affaireId?: string,
		structureId?: string
	): Observable<ITiers> {
		const params = { ...tiers, affaireId, structureId };
		return this.http.post<ITiers>(`${environment.apiUrl}/t/${this.tenantKey}/tiers/update`, params);
	}

	searchTiers(searchPattern: string, partie1Values: string[], partie2Values: string[], structureId?: string): Observable<ITiers[]> {
		// If we have a serach term, we call the api for result
		if (searchPattern?.trim()) {
			let endpoint = structureId
				? this.http.get<IList<ITiers>>(
						`${environment.apiUrl}/t/${this.tenantKey}/tiers?searchTerm=${searchPattern}&structureId=${structureId}`
					)
				: this.http.get<IList<ITiers>>(`${environment.apiUrl}/t/${this.tenantKey}/tiers?searchTerm=${searchPattern}`);

			return endpoint.pipe(
				map(data => {
					return extractToList<ITiers>(
						filtreTiersListForUniqueTiersPartieValue(
							{
								...data,
								items: data.items.sort((a, b) => `${this.GetDisplayName(a)}`.localeCompare(`${this.GetDisplayName(b)}`))
							},
							partie1Values,
							partie2Values
						)
					);
				})
			);
		} // we got an empty result
		else {
			return of([]);
		}
	}

	searchTiersForOperation(
		searchPattern: string,
		partie1Values: string[] = [],
		partie2Values: string[] = [],
		structureId?: string
	): Observable<ITiers[]> {
		// If we have a serach term, we call the api for result
		if (searchPattern?.trim()) {
			let endpoint = structureId
				? this.http.get<IList<ITiers>>(
						`${environment.apiUrl}/t/${this.tenantKey}/tiers?searchTerm=${searchPattern}&structureId=${structureId}`
					)
				: this.http.get<IList<ITiers>>(`${environment.apiUrl}/t/${this.tenantKey}/tiers?searchTerm=${searchPattern}`);

			const unionPartie: string[] = Array.from(new Set([...partie1Values, ...partie2Values]));

			return endpoint.pipe(
				map(data => {
					return extractToList<ITiers>(
						filtreTiersListForOperation(
							{
								...data,
								// items: data.items.sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`))
								items: data.items.sort((a, b) => {
									const aInItemIds = unionPartie.includes(a.id) ? 0 : 1;
									const bInItemIds = unionPartie.includes(b.id) ? 0 : 1;

									// First, prioritize tiers that are in partie1/partie2
									if (aInItemIds !== bInItemIds) {
										return aInItemIds - bInItemIds;
									}

									// Then, sort
									return `${this.GetDisplayName(a)} `.localeCompare(`${this.GetDisplayName(b)}`);
								})
							},
							partie1Values,
							partie2Values
						)
					);
				})
			);
		} // we got an empty result
		else {
			return of([]);
		}
	}

	determineIfIsTiersPhysique(toBeDetermined: any): toBeDetermined is ITiersPersonnePhysiqueForm {
		if ((toBeDetermined as ITiersPersonnePhysiqueForm)?.type === TiersType.PersonnePhysique) {
			return true;
		}
		return false;
	}

	searchDuplicateTiers(tiersForm: ITiersPersonnePhysiqueForm | ITiersPersonneMoraleForm, structureId?: string): Observable<ITiers[]> {
		let httpParams = new HttpParams();

		httpParams = httpParams.append('type', tiersForm.type);
		httpParams = structureId ? httpParams.append('structureId', structureId) : httpParams;

		if (tiersForm.type == TiersType.PersonnePhysique) {
			httpParams = httpParams.append('nom', (tiersForm as ITiersPersonnePhysiqueForm).nom);
			httpParams = httpParams.append('prenom', (tiersForm as ITiersPersonnePhysiqueForm).prenom);
		} else if (tiersForm.type == TiersType.PersonneMorale) {
			httpParams = httpParams.append('libelle', (tiersForm as ITiersPersonneMoraleForm).libelle);
		}
		return this.http.get<ITiers[]>(`${environment.apiUrl}/t/${this.tenantKey}/tiers/getDuplicatedTiers`, { params: httpParams });
	}

	GetDisplayName(tiers: ITiers): string {
		let displayName;
		if (tiers.type == TiersType.PersonneMorale) {
			displayName = tiers.formeJuridique ? `${tiers.formeJuridique} ${tiers.libelle}` : tiers.libelle;
		} else {
			displayName = `${tiers.nom} ${tiers.prenom}`;
		}
		return displayName;
	}

	getNameBySiren(siren: string): Observable<INameBySiret> {
		return this.http.get<INameBySiret>(`${environment.apiUrl}/entreprise/${siren}/name`);
	}

	getCoordonneesBancairesByTiers(tiersId: string): Observable<ICoordonneeBancaire> {
		let httpParams = new HttpParams();
		httpParams = httpParams.append('tiersId', tiersId);
		return this.http.get<ICoordonneeBancaire>(`${environment.apiUrl}/t/${this.tenantKey}/tiers/getCoordonneesBancairesByTiers`, { params: httpParams });
	}


}
